import { useQueue } from '../useQueue'
import { AnyQueueData, QueueJobKind, QueueStatusData, truncateRequestId } from './queueTypes'
import useDocumentGenerationCancel from '../document/useDocumentGenerationCancel'

/**
 * Cancels a job of one specific kind, on top of the generic cancellation below. Only kinds that have something to
 * clean up on the server need one.
 */
export type QueueCancel<StatusT = object> = (requestId: string, graceful: boolean, status?: QueueStatusData<StatusT>) => void

/**
 * Cancels a queued job.
 *
 * A graceful cancellation waits for the server to confirm the deletion before the job leaves the queue, so the toast
 * can still report a failure. A non-graceful one (a restart) drops the job immediately, because the caller is about
 * to dispatch a fresh attempt and is not interested in the old one's status updates.
 */
export default function useQueueCancel(): (requestId: string, graceful: boolean, job?: AnyQueueData) => void {
    const queue = useQueue()
    const cancelHandlers: Partial<Record<QueueJobKind, QueueCancel<any>>> = {
        [QueueJobKind.Document]: useDocumentGenerationCancel(),
    }

    return (requestId: string, graceful: boolean, job?: AnyQueueData) => {
        const id = truncateRequestId(requestId)

        // Abort the outgoing request if it has not been enqueued on the server yet.
        job?.context?.abortController?.abort()

        if (!graceful) {
            // Drop it straight away when we are not waiting for a delete request to finish.
            queue.remove(id)
        }

        cancelHandlers[job?.kind]?.(id, graceful, job?.status)
    }
}
